import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import './App.css';
import ResumeForm from './components/ResumeForm';
import ResumePreview from './components/ResumePreview';
import FullPagePreview from './components/FullPagePreview';
import TemplateGallery from './components/TemplateGallery';
import Loader from './components/Loader';
import ThemeToggle from './components/ThemeToggle';
import Toast from './components/Toast';
import ResumeManager from './components/ResumeManager';
import WelcomePopup from './components/WelcomePopup';
import { getEmptyResumeData, getSampleResumeData } from './utils/defaultData';

const AppContent = ({ resumeData, setResumeData, template, setTemplate,
  layout, setLayout, sectionVisibility, setSectionVisibility,
  toasts, addToast, removeToast, isDarkMode, setIsDarkMode,
  loading, resumes, setResumes, activeResumeId, setActiveResumeId,
  sectionOrder, setSectionOrder }) => {
  const location = useLocation();
  const isPreviewPage = location.pathname === '/preview';
  const [showTemplateGallery, setShowTemplateGallery] = useState(false);
  const [showResumeManager, setShowResumeManager] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const autoSaveTimer = useRef(null);
  const [saveStatus, setSaveStatus] = useState('saved');

  useEffect(() => {
    const timer = setTimeout(() => setShowWelcome(true), 3000);
    return () => clearTimeout(timer);
  }, []);

  const handleUpdateData = useCallback((newData) => {
    setResumeData(newData);
    setSaveStatus('saving');
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      const allResumes = JSON.parse(localStorage.getItem('allResumes') || '{}');
      allResumes[activeResumeId] = { data: newData, template, layout, sectionVisibility, sectionOrder };
      localStorage.setItem('allResumes', JSON.stringify(allResumes));
      setSaveStatus('saved');
    }, 500);
  }, [activeResumeId, template, layout, sectionVisibility, setResumeData]);

  const handleClearForm = useCallback(() => {
    setResumeData(getEmptyResumeData());
    setTemplate('pro');
    setLayout('single');
    setSectionVisibility({ summary: true, experience: true, education: true, skills: true, projects: true, certifications: true, languages: true, interests: true });
    setSectionOrder(['personalInfo', 'summary', 'education', 'skills', 'professionalExperience', 'projects', 'certifications', 'awards']);
    addToast('Form cleared', 'info');
  }, [setResumeData, setTemplate, setLayout, setSectionVisibility, setSectionOrder, addToast]);

  const handleTemplateChange = useCallback((newTemplate) => {
    setTemplate(newTemplate);
    addToast(`Template changed to ${newTemplate}`, 'success');
  }, [setTemplate, addToast]);

  const handleLayoutChange = useCallback((newLayout) => {
    setLayout(newLayout);
    addToast(`Layout changed to ${newLayout}`, 'success');
  }, [setLayout, addToast]);

  const handleToggleSection = useCallback((section) => {
    setSectionVisibility(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  }, [setSectionVisibility]);

  const handleReorderSection = useCallback((fromIndex, toIndex) => {
    setSectionOrder(prev => {
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  }, [setSectionOrder]);

  const handleResetOrder = useCallback(() => {
    setSectionOrder(['personalInfo', 'summary', 'education', 'skills', 'professionalExperience', 'projects', 'certifications', 'awards']);
    addToast('Section order reset to default', 'info');
  }, [setSectionOrder, addToast]);

  const handleNewResume = useCallback(() => {
    const id = 'resume_' + Date.now();
    const newResume = { id, name: `Resume ${resumes.length + 1}`, data: getEmptyResumeData(), template: 'pro', layout: 'single-column', sectionVisibility: {} };
    setResumes(prev => [...prev, newResume]);
    setActiveResumeId(id);
    setResumeData(getEmptyResumeData());
    setTemplate('pro');
    addToast('New resume created', 'success');
  }, [resumes.length, setResumes, setActiveResumeId, setResumeData, setTemplate, addToast]);

  const handleSwitchResume = useCallback((id) => {
    const r = resumes.find(r => r.id === id);
    if (r) {
      setActiveResumeId(id);
      setResumeData(r.data);
      setTemplate(r.template || 'pro');
      setLayout(r.layout || 'single-column');
      if (r.sectionVisibility) setSectionVisibility(r.sectionVisibility);
      if (r.sectionOrder) setSectionOrder(r.sectionOrder);
      addToast(`Switched to ${r.name}`, 'info');
    }
  }, [resumes, setActiveResumeId, setResumeData, setTemplate, setLayout, setSectionVisibility, setSectionOrder, addToast]);

  const handleDeleteResume = useCallback((id) => {
    if (resumes.length <= 1) { addToast('Cannot delete last resume', 'warning'); return; }
    const newResumes = resumes.filter(r => r.id !== id);
    setResumes(newResumes);
    if (activeResumeId === id) {
      handleSwitchResume(newResumes[0].id);
    }
    const allResumes = JSON.parse(localStorage.getItem('allResumes') || '{}');
    delete allResumes[id];
    localStorage.setItem('allResumes', JSON.stringify(allResumes));
    addToast('Resume deleted', 'info');
  }, [resumes, activeResumeId, setResumes, handleSwitchResume, addToast]);

  // Sync resumes state array when data changes
  useEffect(() => {
    setResumes(prev => prev.map(r =>
      r.id === activeResumeId ? { ...r, data: resumeData, template, layout, sectionVisibility, sectionOrder } : r
    ));
  }, [resumeData, template, layout, sectionVisibility, sectionOrder, activeResumeId, setResumes]);

  useEffect(() => {
    return () => {
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    };
  }, []);

  if (loading) return <Loader />;

  const saveIcons = { saved: '✓', saving: '⋯', error: '✕' };

  return (
    <>
      <div className="toast-container">
        {toasts.map(t => (
          <Toast key={t.id} message={t.message} type={t.type} onClose={() => removeToast(t.id)} />
        ))}
      </div>

      {showTemplateGallery && (
        <TemplateGallery
          current={template}
          onSelect={(t) => { handleTemplateChange(t); setShowTemplateGallery(false); }}
          onClose={() => setShowTemplateGallery(false)}
        />
      )}

      {showWelcome && <WelcomePopup onClose={() => setShowWelcome(false)} />}

      {showResumeManager && (
        <ResumeManager
          resumes={resumes}
          activeId={activeResumeId}
          onSwitch={handleSwitchResume}
          onNew={handleNewResume}
          onDelete={handleDeleteResume}
          onRename={(id, name) => {
            setResumes(prev => prev.map(r => r.id === id ? { ...r, name } : r));
          }}
          onClose={() => setShowResumeManager(false)}
        />
      )}

      {isPreviewPage ? (
        <FullPagePreview data={resumeData} template={template} layout={layout}
          sectionVisibility={sectionVisibility} sectionOrder={sectionOrder} />
      ) : (
        <div className="app">
          <header className="app-header">
            <div className="header-left">
              <Link to="/" className="app-logo">
                <span className="logo-icon">✧</span>
                <span className="logo-text">Resume<span className="logo-accent">TohBnale</span></span>
              </Link>
            </div>

            <div className="header-center">
              <nav className="header-tabs">
                <button
                  className={`header-tab ${!showTemplateGallery && location.pathname === '/' ? 'active' : ''}`}
                  onClick={() => setShowTemplateGallery(false)}
                >
                  <span className="tab-icon">✎</span>
                  Editor
                </button>
                <button
                  className={`header-tab ${showTemplateGallery ? 'active' : ''}`}
                  onClick={() => setShowTemplateGallery(true)}
                >
                  <span className="tab-icon">◈</span>
                  Templates
                </button>
                <button
                  className="header-tab"
                  onClick={() => setShowResumeManager(true)}
                >
                  <span className="tab-icon">☰</span>
                  Resumes
                </button>
              </nav>
            </div>

            <div className="header-right">
              <div className="save-status" title={`Auto-save: ${saveStatus}`}>
                <span className={`save-icon save-${saveStatus}`}>{saveIcons[saveStatus]}</span>
                <span className="save-label">{saveStatus === 'saved' ? 'Saved' : saveStatus === 'saving' ? 'Saving...' : 'Error'}</span>
              </div>
              <a href="https://www.linkedin.com/in/abhishek-maurya-707106158/"
                target="_blank" rel="noopener noreferrer" className="header-linkedin">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                </svg>
              </a>
              <ThemeToggle isDark={isDarkMode} onToggle={() => setIsDarkMode(!isDarkMode)} />
            </div>
          </header>

          <div className="toolbar">
            <div className="toolbar-left">
              <span className="toolbar-label">Layout:</span>
              <button className={`toolbar-btn ${layout === 'single-column' ? 'active' : ''}`}
                onClick={() => handleLayoutChange('single-column')}>Single</button>
              <button className={`toolbar-btn ${layout === 'two-column' ? 'active' : ''}`}
                onClick={() => handleLayoutChange('two-column')}>Two Column</button>
            </div>
            <div className="toolbar-right">
              <span className="toolbar-label">Active Template: <strong>{template}</strong></span>
              <button className="toolbar-btn primary" onClick={() => setShowTemplateGallery(true)}>
                Change Template
              </button>
              <Link to="/preview" className="toolbar-btn preview-link">
                Full Preview
              </Link>
            </div>
          </div>

          <main className="app-main">
            <ResumeForm
              data={resumeData}
              onUpdate={handleUpdateData}
              onClear={handleClearForm}
              sectionVisibility={sectionVisibility}
              onToggleSection={handleToggleSection}
              sectionOrder={sectionOrder}
              onReorderSection={handleReorderSection}
              onResetOrder={handleResetOrder}
            />
            <ResumePreview
              data={resumeData}
              template={template}
              layout={layout}
              sectionVisibility={sectionVisibility}
              sectionOrder={sectionOrder}
              addToast={addToast}
            />
          </main>

          <footer className="app-footer">
            <span>© 2026 Abhishek Maurya. Built with passion.</span>
          </footer>
        </div>
      )}
    </>
  );
};

function App() {
  const [loading, setLoading] = useState(true);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [template, setTemplate] = useState('pro');
  const [layout, setLayout] = useState('single-column');
  const [sectionVisibility, setSectionVisibility] = useState({
    personalInfo: true, summary: true, education: true, skills: true,
    professionalExperience: true, projects: true, certifications: true, awards: true
  });
  const [sectionOrder, setSectionOrder] = useState([
    'personalInfo', 'summary', 'education', 'skills',
    'professionalExperience', 'projects', 'certifications', 'awards'
  ]);
  const [resumes, setResumes] = useState([]);
  const [activeResumeId, setActiveResumeId] = useState('default');
  const [resumeData, setResumeData] = useState(getSampleResumeData());
  const [toasts, setToasts] = useState([]);
  const toastCounter = useRef(0);

  const addToast = useCallback((message, type = 'success') => {
    const id = ++toastCounter.current;
    setToasts(prev => [...prev, { id, message, type }]);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem('allResumes');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        const ids = Object.keys(parsed);
        if (ids.length > 0) {
          const resumeList = ids.map(id => ({
            id,
            name: parsed[id].name || id,
            data: parsed[id].data || getSampleResumeData(),
            template: parsed[id].template || 'pro',
            layout: parsed[id].layout || 'single-column',
            sectionVisibility: parsed[id].sectionVisibility || {},
            sectionOrder: parsed[id].sectionOrder
          }));
          setResumes(resumeList);
          setActiveResumeId(resumeList[0].id);
          setResumeData(resumeList[0].data);
          setTemplate(resumeList[0].template || 'pro');
          setLayout(resumeList[0].layout || 'single-column');
          if (resumeList[0].sectionVisibility) setSectionVisibility(resumeList[0].sectionVisibility);
          if (resumeList[0].sectionOrder) setSectionOrder(resumeList[0].sectionOrder);
        }
      } catch (e) { /* ignore corrupt data */ }
    }
    setTimeout(() => setLoading(false), 1500);
  }, []);

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.setAttribute('data-theme', 'dark');
      document.body.setAttribute('data-theme', 'dark');
    } else {
      document.documentElement.removeAttribute('data-theme');
      document.body.removeAttribute('data-theme');
    }
  }, [isDarkMode]);

  return (
    <Router>
      <Routes>
        <Route path="/*" element={
          <AppContent
            resumeData={resumeData} setResumeData={setResumeData}
            template={template} setTemplate={setTemplate}
            layout={layout} setLayout={setLayout}
            sectionVisibility={sectionVisibility} setSectionVisibility={setSectionVisibility}
            toasts={toasts} addToast={addToast} removeToast={removeToast}
            isDarkMode={isDarkMode} setIsDarkMode={setIsDarkMode}
            loading={loading}
            resumes={resumes} setResumes={setResumes}
            activeResumeId={activeResumeId} setActiveResumeId={setActiveResumeId}
            sectionOrder={sectionOrder} setSectionOrder={setSectionOrder}
          />
        } />
      </Routes>
    </Router>
  );
}

export default App;
